import { useEffect, useState } from 'react';

// Detects active browser-level translation (Chrome / Edge / Firefox / Safari).
// - Chrome / Edge with Google Translate (and the GT extension on any browser) set
//   `translated-ltr` / `translated-rtl` on <html>.
// - Edge built-in, Firefox 118+ built-in and Safari built-in translators all
//   replace the textContent of rendered nodes. We observe a hidden sentinel
//   rendered by `root.tsx` (kept in React's tree so we don't mutate
//   `document.body` directly — `hydrateRoot(document, …)` is sensitive to that).
//   Once any of those engines runs, the sentinel's textContent diverges from
//   the known-original value.
export const BROWSER_TRANSLATION_SENTINEL_ATTR = 'data-browser-translation-sentinel';
// Compared against the sentinel's textContent. Using a fixed constant (instead
// of reading the DOM on mount) avoids a race where a translator runs before
// hydration and we'd otherwise capture the already-translated text as the
// baseline.
export const BROWSER_TRANSLATION_SENTINEL_TEXT = 'Übersetzung';

const SENTINEL_SELECTOR = `[${BROWSER_TRANSLATION_SENTINEL_ATTR}]`;

export function useBrowserTranslationActive(): boolean {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    const html = document.documentElement;
    const sentinel = document.querySelector(SENTINEL_SELECTOR) as HTMLElement | null;

    const check = () => {
      const googleTranslated =
        html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl');
      const sentinelChanged =
        !!sentinel && sentinel.textContent?.trim() !== BROWSER_TRANSLATION_SENTINEL_TEXT;
      setIsActive(googleTranslated || sentinelChanged);
    };

    check();

    const observer = new MutationObserver(check);
    observer.observe(html, { attributes: true, attributeFilter: ['class', 'lang'] });
    if (sentinel) {
      observer.observe(sentinel, { childList: true, characterData: true, subtree: true });
    }

    return () => observer.disconnect();
  }, []);

  return isActive;
}
